import { RCALibraryColumnsIdEnum } from "pages/Corporate/RCA/RCALibrary/components/useRCALibraryColumns/useRCALibraryColumnsInterfaces";
import { ICreateEditManualRCAModalFormValues } from "pages/Corporate/RCA/RCALibrary/components/RCALibraryDetail/components/CreateEditManualRCAModal/CreateEditManualRCAModalInterfaces";
import { IRCALibraryTableOperationFilters } from "./RCALibraryEntityInterfaces";
import { FAILURE_TYPES_CODE_EXCEPTIONS } from "constants/constants";

/** Order value for Hasura order_by (asc | desc) */
export type OrderDirection = "asc" | "desc";

/** Shape of the order_by variable for getRCALibrary / getRCALibraryCount queries */
export interface RCALibraryOrderByVariable {
    sequence_id?: OrderDirection;
    incident_description?: OrderDirection;
    due_date?: OrderDirection;
    date_held?: OrderDirection;
    status?: { order: OrderDirection };
    owner?: { first_name: OrderDirection };
}

/**
 * Builds the order_by object for RCA library list queries from table operation filters.
 * Used by GetRCALibrary and GetUserRCAs to avoid duplicating the column-to-field mapping.
 */
export function getRCALibraryOrderBy(
    tableOperationFilters: IRCALibraryTableOperationFilters,
): RCALibraryOrderByVariable {
    const order = tableOperationFilters.order as OrderDirection;
    return {
        ...(tableOperationFilters.orderBy === RCALibraryColumnsIdEnum.NUMBER && {
            sequence_id: order,
        }),
        ...(tableOperationFilters.orderBy === RCALibraryColumnsIdEnum.DESCRIPTION && {
            incident_description: order,
        }),
        ...(tableOperationFilters.orderBy === RCALibraryColumnsIdEnum.DUE_DATE && {
            due_date: order,
        }),
        ...(tableOperationFilters.orderBy === RCALibraryColumnsIdEnum.STATUS && {
            status: { order },
        }),
        ...(tableOperationFilters.orderBy === RCALibraryColumnsIdEnum.COMPLETED && {
            date_held: order,
        }),
        ...(tableOperationFilters.orderBy === RCALibraryColumnsIdEnum.OWNER && {
            owner: { first_name: order },
        }),
    };
}

/** Where fragment for failure type/code queries: machine center scope */
export interface MachineCenterConditionWhere {
    non_location_specific_machine_center_failure_codes: {
        machine_center:
            | { machine_center_id: { _eq: number } }
            | { production_line_id: { _eq: number } }
            | { mill_id: { _eq: number } };
    };
}

/**
 * Builds the machine center condition used in failure type and failure code option queries.
 * Returns undefined if no relevant option (machineCenterId, productionLineId, millId) is selected.
 */
export function createMachineCenterCondition(
    optionsSelected: ICreateEditManualRCAModalFormValues,
): MachineCenterConditionWhere | undefined {
    const machineCenterId =
        optionsSelected?.machineCenterId != null ? Number(optionsSelected.machineCenterId) : undefined;
    const productionLineId =
        optionsSelected?.productionLineId != null ? Number(optionsSelected.productionLineId) : undefined;
    const millId = optionsSelected?.millId != null ? Number(optionsSelected.millId) : undefined;

    if (machineCenterId != null) {
        return {
            non_location_specific_machine_center_failure_codes: {
                machine_center: { machine_center_id: { _eq: machineCenterId } },
            },
        };
    }
    if (productionLineId != null) {
        return {
            non_location_specific_machine_center_failure_codes: {
                machine_center: { production_line_id: { _eq: productionLineId } },
            },
        };
    }
    if (millId != null) {
        return {
            non_location_specific_machine_center_failure_codes: {
                machine_center: { mill_id: { _eq: millId } },
            },
        };
    }
    return undefined;
}

/** Machine center filter in functional location condition (one of id, production line, or mill) */
type FunctionalLocationAreaMachineCenter =
    | { machine_center_id: { _eq: number } }
    | { production_line_id: { _eq: number } }
    | { mill_id: { _eq: number } };

/** Where fragment for failure type/code queries: functional location scope */
export interface FunctionalLocationConditionWhere {
    location_specific_functional_location_failure_codes: {
        functional_location: {
            area: {
                disabled_at: { _is_null: true };
                machine_center?: FunctionalLocationAreaMachineCenter;
            };
            functional_location_id?: { _eq: number };
            disabled_at: { _is_null: true };
            deleted_at: { _is_null: true };
        };
    };
}

/**
 * Builds the functional location condition used in failure type and failure code option queries.
 * Returns undefined if no relevant option is selected.
 */
export function createFunctionalLocationCondition(
    optionsSelected: ICreateEditManualRCAModalFormValues,
): FunctionalLocationConditionWhere | undefined {
    const functionalLocationId =
        optionsSelected?.functionalLocationId != null
            ? Number(optionsSelected.functionalLocationId)
            : undefined;
    const machineCenterId =
        optionsSelected?.machineCenterId != null ? Number(optionsSelected.machineCenterId) : undefined;
    const productionLineId =
        optionsSelected?.productionLineId != null ? Number(optionsSelected.productionLineId) : undefined;
    const millId = optionsSelected?.millId != null ? Number(optionsSelected.millId) : undefined;

    if (functionalLocationId != null) {
        return {
            location_specific_functional_location_failure_codes: {
                functional_location: {
                    area: { disabled_at: { _is_null: true } },
                    functional_location_id: { _eq: functionalLocationId },
                    disabled_at: { _is_null: true },
                    deleted_at: { _is_null: true },
                },
            },
        };
    }
    if (machineCenterId != null) {
        return {
            location_specific_functional_location_failure_codes: {
                functional_location: {
                    disabled_at: { _is_null: true },
                    deleted_at: { _is_null: true },
                    area: {
                        disabled_at: { _is_null: true },
                        machine_center: { machine_center_id: { _eq: machineCenterId } },
                    },
                },
            },
        };
    }
    if (productionLineId != null) {
        return {
            location_specific_functional_location_failure_codes: {
                functional_location: {
                    disabled_at: { _is_null: true },
                    deleted_at: { _is_null: true },
                    area: {
                        disabled_at: { _is_null: true },
                        machine_center: { production_line_id: { _eq: productionLineId } },
                    },
                },
            },
        };
    }
    if (millId != null) {
        return {
            location_specific_functional_location_failure_codes: {
                functional_location: {
                    disabled_at: { _is_null: true },
                    deleted_at: { _is_null: true },
                    area: {
                        disabled_at: { _is_null: true },
                        machine_center: { mill_id: { _eq: millId } },
                    },
                },
            },
        };
    }
    return undefined;
}

/** Where clause used to exclude certain failure type codes in RCA details options */
export interface FailureTypesExceptionsWhere {
    _and: Array<
        | { _or: Array<{ code: { _neq: string } } | { code: { _is_null: true } }> }
        | { deleted_at: { _is_null: true } }
    >;
}

/**
 * Static where clause for failure type options: excludes BELOW_TRIGGER, NOT_SELECTED, and deleted.
 */
export const FAILURE_TYPES_EXCEPTIONS_WHERE: FailureTypesExceptionsWhere = {
    _and: [
        {
            _or: [
                { code: { _neq: FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER } },
                { code: { _is_null: true } },
            ],
        },
        {
            _or: [
                { code: { _neq: FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED } },
                { code: { _is_null: true } },
            ],
        },
        { deleted_at: { _is_null: true } },
    ],
};
